import { useEffect, useMemo, useState } from "react";
import { styled } from "@pigment-css/react";
import { CrossLarge, PencilLine } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { Todo } from "@acto/taskpane/core/domain";
import type { Meeting } from "@acto/taskpane/core/domain";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { CustomerCard } from "@acto/taskpane/field-sales-2/components/patterns/CustomerCard";
import { useNoteTodos } from "@acto/taskpane/core/data-access/todos/useNoteTodos";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import type { RecapNote, RecapTodoEdit } from "@acto/taskpane/field-sales-2/data-access/recap";
import { useEditRecapTodo } from "@acto/taskpane/field-sales-2/data-access/recap";
import { RecapReadView } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapReadView";
import { pickerDateToDomain, RecapEditSummaryScreen, RecapEditTodoScreen } from "./RecapEditing";
import { PrimaryActionButton } from "./ui/PrimaryActionButton";

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

// Sticky to the bottom and above the scrolling content (matching the top bar's sticky/zIndex), so
// "Weiter" is always reachable. The to-top gradient fades the content out behind it as it scrolls
// under — same treatment as the recap edit screens' footer.
const Footer = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  display: "flex",
  justifyContent: "center",
  // Generous top padding so the fade extends well above the button and is actually visible as the
  // content scrolls under it (mirroring the top bar's tall gradient zone); the solid stop sits below
  // the button so the button always reads against a solid background.
  padding: `${theme.fieldSalesTwo.spacing[44]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 60%, rgba(240, 238, 237, 0))`,
}));

// How long the check-off animation runs before the deleted todo is removed from the list.
const CHECK_OFF_MS = 360;

interface RecapSummaryScreenProps {
  /** The committed recap note — supplies the note id for the todo read. */
  note: RecapNote;
  /** This submission's AI summary. Null when none was generated, in which case the transcript is shown. */
  summaryText?: string | null;
  /** The meeting just recapped — supplies the expanded customer-card header (date / name / ref). */
  meeting: Meeting;
  /** Suppress the card's date badge — ad-hoc recaps reference no meeting (synthetic date). */
  hideDate?: boolean;
  /** Show the scheduled time (HH:mm) in the card's badge. */
  showBadgeTime?: boolean;
  /** Save edited recap content — persists it and re-triggers AI regeneration. */
  onEditRecapContent: (content: string) => void;
  /** An edit whose save failed. Reopens the editor on that text instead of the saved content. */
  pendingEditContent?: string | null;
  /** Drop the failed edit, so leaving the editor does not resurrect it later. */
  onDiscardPendingEdit?: () => void;
  /** Footer "Weiter": REMOVE this meeting from the stack and advance (or close). */
  onContinue: () => void;
  /** Close the whole flow. */
  onClose: () => void;
}

/** What's currently being edited as a full-screen overlay over the summary, if anything. */
type EditTarget = { kind: "summary" } | { kind: "todo"; id: string } | null;

/** Clone a domain Todo with an edit applied, so the list + editor reflect unsaved changes. */
function applyTodoEdit(todo: Todo, edit: RecapTodoEdit | undefined): Todo {
  if (!edit) return todo;
  return new Todo(
    todo.id,
    todo.title,
    todo.status,
    edit.description,
    edit.dueDate ? pickerDateToDomain(edit.dueDate) : undefined,
    todo.processedReason,
    todo.isWorkflow,
    todo.isNoteBased,
    todo.client,
    edit.assignee ?? todo.assignee,
  );
}

/**
 * "Summary" screen. Reads the note's todos via the core `useNoteTodos` (note-keyed), shows the AI
 * summary, and is the entry to editing: the pencil opens the recap editor; tapping a todo opens
 * the todo editor (both full-screen overlays). Todo edits/deletes persist via `useEditRecapTodo`
 * and are reflected optimistically. Editing the recap content re-triggers AI regeneration via
 * `onEditRecapContent`. Deleting a todo returns here and animates it out (check-off).
 * "Weiter" removes this meeting from the batch via `onContinue`.
 */
export function RecapSummaryScreen({
  note,
  meeting,
  hideDate,
  showBadgeTime,
  summaryText,
  onEditRecapContent,
  pendingEditContent,
  onDiscardPendingEdit,
  onContinue,
  onClose,
}: RecapSummaryScreenProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const { data: todos, refetch: refetchTodos } = useNoteTodos({ noteId: note.id });
  const { saveTodo, deleteTodo } = useEditRecapTodo();

  const track = useTrack();
  // The resolved to-do list here is the "were to-dos created with this note" answer that
  // NOTE_CREATED cannot carry (extraction happens after save). Keyed on the note so a batch
  // flow fires once per recap.
  useViewTrackedOnce(
    () => {
      track(AnalyticsEvents.RECAP_SUMMARY_GENERATED, {
        noteId: note.id,
        customerId: meeting.customer.id,
        customerName: meeting.customer.name,
        todosCreated: (todos ?? []).length > 0,
        todosCreatedCount: (todos ?? []).length,
      });
    },
    todos === undefined ? null : note.id,
  );

  const [editTarget, setEditTarget] = useState<EditTarget>(pendingEditContent ? { kind: "summary" } : null);
  // Local optimistic overlays for todo edits until the note-todo subscription reconciles.
  const [todoEdits, setTodoEdits] = useState<Record<string, RecapTodoEdit>>({});
  const [checkingOffIds, setCheckingOffIds] = useState<string[]>([]); // animating out
  const [removedIds, setRemovedIds] = useState<string[]>([]); // gone after the animation

  // `note` is the save response, whose summary is never this submission's: null for a new note,
  // the previous content's for a re-commit. The transcript is the only truthful fallback.
  const summary = summaryText ?? note.content;
  const customerName = meeting.customer.name;
  const customerReference = meeting.customer.externalReference;

  // Editing the recap content re-runs the backend summarize, which regenerates this note's todos.
  // useNoteTodos is cache-first and keyed on the unchanged note id, so it would otherwise keep serving
  // the stale pre-edit list. A changed summary means regeneration ran — force a network-only refetch so
  // the regenerated todos replace the cached ones. (GraphQL subscriptions are not wired in this client,
  // so a live noteTodoChanged subscription can't be relied on here.)
  useEffect(() => {
    refetchTodos?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const displayTodos = useMemo(
    () => (todos ?? []).filter((t) => !removedIds.includes(t.id)).map((t) => applyTodoEdit(t, todoEdits[t.id])),
    [todos, removedIds, todoEdits],
  );

  const handleDeleteTodo = (id: string) => {
    setEditTarget(null); // return to the summary view
    setCheckingOffIds((prev) => [...prev, id]); // play the check-off animation
    void deleteTodo(id).then((ok) => {
      if (!ok) {
        setCheckingOffIds((prev) => prev.filter((x) => x !== id)); // restore the row on failure
        return;
      }
      window.setTimeout(() => {
        setRemovedIds((prev) => [...prev, id]);
        setCheckingOffIds((prev) => prev.filter((x) => x !== id));
      }, CHECK_OFF_MS);
    });
  };

  if (editTarget?.kind === "summary") {
    return (
      <RecapEditSummaryScreen
        customerName={customerName}
        customerReference={customerReference}
        aiSummary={summaryText ?? ""}
        content={note.content}
        initialDraft={pendingEditContent ?? undefined}
        onBack={() => {
          setEditTarget(null);
          onDiscardPendingEdit?.();
        }}
        onSave={(content) => {
          setEditTarget(null);
          onEditRecapContent(content);
        }}
      />
    );
  }

  if (editTarget?.kind === "todo") {
    const todo = displayTodos.find((t) => t.id === editTarget.id);
    if (todo) {
      return (
        <RecapEditTodoScreen
          customerName={customerName}
          customerReference={customerReference}
          todo={todo}
          onBack={() => setEditTarget(null)}
          onSave={(changes) => {
            setTodoEdits((prev) => ({ ...prev, [todo.id]: changes }));
            setEditTarget(null);
            void saveTodo(todo.id, changes).then((ok) => {
              if (!ok) {
                // Roll the optimistic overlay back to server state on failure.
                setTodoEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== todo.id)));
                return;
              }
              // Sync the cache to the saved state so a later remount doesn't flash the pre-edit todo.
              refetchTodos?.();
            });
          }}
          onDelete={() => handleDeleteTodo(todo.id)}
        />
      );
    }
  }

  return (
    <Page>
      <TopNav
        leading={
          <HeaderIconButton type="button" aria-label={t("a11y.close")} onClick={onClose}>
            <CrossLarge width={18} height={18} />
          </HeaderIconButton>
        }
        trailing={
          <HeaderIconButton
            type="button"
            aria-label={t("a11y.edit")}
            onClick={() => setEditTarget({ kind: "summary" })}
          >
            <PencilLine width={18} height={18} />
          </HeaderIconButton>
        }
      />

      <Body>
        <CustomerCard
          meeting={meeting}
          hideDate={hideDate}
          showBadgeTime={showBadgeTime}
          expanded
          summary={summary}
          onToggleExpand={undefined}
        />

        <RecapReadView
          todos={displayTodos}
          checkingOffIds={checkingOffIds}
          onSelectTodo={(id) => setEditTarget({ kind: "todo", id })}
        />
      </Body>

      <Footer>
        <PrimaryActionButton type="button" onClick={onContinue}>
          {t("summary.continue")}
        </PrimaryActionButton>
      </Footer>
    </Page>
  );
}
